import * as React from 'react';
import { useSWRConfig } from "swr";
import { mutateUrls } from "../../../lib/useGenericSWR";
import {
    Alert,
    Box,
    FormGroup,
    FormControlLabel,
    Snackbar,
    Switch,
    Link
} from '@mui/material';
import { useFormik } from 'formik';
import { useManage2faAuthentication, useForceMfaStatus } from './Manage2faEndpoints';
import axios from 'axios';
import Manage2faOptions from './Manage2faOptions';
import { getUrl, useDomainNavigate, domain } from '../../../domain';
import { v4 as uuidv4 } from 'uuid';
import { logErrorGrayLog } from '../../../lib/ClientErrorGrayLogger';

export default function Manage2fa() {
    const swrConfig = useSWRConfig();
    const [reMutateUrls, setReMutateUrls] = React.useState(false);
    const authentication = useManage2faAuthentication();
    const forceMfaStatus = useForceMfaStatus();
    const [openConfirmation, setOpenConfirmation] = React.useState(false);
    const [errors, setErrors] = React.useState([]);
    const [twoFactorEnabled, setTwoFactorEnabled] = React.useState(false);
    const [initialLoad, setInitialLoad] = React.useState(true);
    let navigate = useDomainNavigate();

    const handleChangeTwoFactorEnabled = (event) => {
        const newValue = event.target.checked;

        setTwoFactorEnabled(newValue);

        formik.values.twoFactorEnabled = newValue;
        formik.handleSubmit();
    };

    const handleCloseConfirmation = (event, reason) => {
        if (reason === 'clickaway') {
          return;
        }

        setOpenConfirmation(false);
    };


    const axiosconfig = {
        headers: {
            "Content-Type": "application/json",
            "SubDomain": domain.subDomain
        },
    };

    const initialValues = {
        twoFactorEnabled: twoFactorEnabled
    };

    const formik = useFormik({
        initialValues: initialValues,
        onSubmit: (values) => {
            
            axios.post(getUrl(`/identity/2fa/manage/general`), JSON.stringify(values), axiosconfig)
                .then(response => {
                    formik.setSubmitting(false);

                    if (response.status === 200) {
                        setOpenConfirmation(true);
                    }
                    else  {
                        setErrors(response);
                    }
                })
                .catch(error => {                    
                    if (Array.isArray(error.response.data)) {
                        setErrors(error.response.data);                    
                    }
                    else {
                        if (error?.response?.data?.errorMessage) {
                            setErrors([error?.response?.data?.errorMessage])    
                        }
                        else {
                            console.log("Unexpected error", error?.response, error?.response?.data);
                            const clientError = {
                                referenceGuid: uuidv4(),
                                clientError: JSON.stringify(error),
                                clientStacktrace: "N/A Manage2fa.js",
                                url: window?.location?.href
                            };                        

                            logErrorGrayLog(clientError);
                            setErrors(["Unexpected error: Reference " + clientError.referenceGuid]);
                        }
                    }
                    formik.setSubmitting(false);
                });
        },
    });

    React.useLayoutEffect(() => {
        if (reMutateUrls) {
            mutateUrls(swrConfig);
            setReMutateUrls(false);
        }
    }, [reMutateUrls, swrConfig]);

    React.useEffect(() => {
        if (initialLoad && !authentication.isLoading) {
            setTwoFactorEnabled(authentication?.result?.twoFactorEnabled)
            setInitialLoad(false);

            if (authentication?.result === undefined) {
                navigate("/account/login");
            }
        }
    }, [initialLoad, authentication, navigate])

    return (
        <>
        <Box
            sx={{
                marginTop: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
            }}
            >
                <Alert severity="info">
                    Manage your Multi-factor authentication (MFA) methods here
                </Alert>
                {(authentication.result?.phoneNumber === null || authentication.result?.phoneNumber === "") && (
                    <Alert sx={{ mt: 2 }} severity="error">
                        You must supply a Phone Number in order to use SMS for Multi-factor Authentication. Edit your profile <Link href={getUrl("/account/manage/profile")}>here</Link>
                    </Alert>
                )}

                <form style={{width: '100%'}}
                >
                    {
                        errors?.map((error) => (
                            <Alert sx={{ mt: 2 }} severity="error">{error}</Alert>
                        ))
                    }

                    {/* Hide toggle if ForceMFA is enabled and user has MFA enabled */}
                    {!(forceMfaStatus?.result?.forceMfa && twoFactorEnabled) && (
                        <FormGroup sx={{ mt: 2 }}>
                            <FormControlLabel
                                name="twoFactorEnabled"
                                control={
                                    <Switch readOnly
                                        checked={twoFactorEnabled}
                                        onChange={handleChangeTwoFactorEnabled}
                                    />
                                }
                                label={`Multi-factor Authentication - ${twoFactorEnabled ? 'Enabled' : 'Disabled'}`}
                            />
                        </FormGroup>
                    )}
                    {/* Show MFA options if user has MFA enabled */}
                    { (twoFactorEnabled) && (
                        <Manage2faOptions
                            phoneNumber={authentication.result?.phoneNumber}
                        />
                    )}
                </form>
            </Box>
            <Snackbar
                open={openConfirmation}
                autoHideDuration={6000}
                onClose={handleCloseConfirmation}
            >
                <Alert severity="success">
                    Your Multi-factor settings were updated successfully.
                </Alert>
            </Snackbar>
        </>
    )
}